import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../src/styles/practicenavbar.css";
import Coursemap from "./Coursecomp";
import { useNavigate } from "react-router-dom";

const MENU_ITEMS = [];

// Single source of truth: just list topic names here
const TOPIC_NAMES = [
  "DSA",
  "C",
  "Python",
  "Java",
  "Javascript",
  "C++",
  "HTML",
  "CSS",
  "Bootstrap",
  "Nodejs",
  "Expressjs",
  "DataScience",
  "DataAnalytics",
  "Reactjs",
  "Mongodb",
  "SQL",
  "Generative AI",
  "Cloud Computing",
  "DevOps",
  "CyberSecurity",
];

// Auto-generate { name: id } map from the array above
const TOPICS = TOPIC_NAMES.reduce((acc, name, index) => {
  acc[name] = index + 1;
  return acc;
}, {});

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="11" cy="11" r="7"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
}

function BurgerIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  );
}

// Dropdown menu item: "Practice MCQs" with a list of every course.
// Selecting a course sets it active and tells the course view to
// jump straight to that course's first quiz-bearing lesson.
function PracticeMcqMenu({ onSelectCourse }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="cm-navbar__dropdown" ref={wrapRef}>
      <button
        type="button"
        className="cm-navbar__menu-item"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((prev) => !prev)}
      >
        Practice MCQs
        <ChevronIcon />
      </button>

      {open && (
        <ul className="cm-navbar__dropdown-list" role="menu">
          {TOPIC_NAMES.map((topic) => (
            <li key={topic} role="none">
              <button
                type="button"
                role="menuitem"
                className="cm-navbar__dropdown-item"
                onClick={() => {
                  onSelectCourse(topic);
                  setOpen(false);
                }}
              >
                {topic}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Search box: filters TOPIC_NAMES as the user types, shows matches in a
// dropdown, and lets them pick one with the mouse, Enter, or arrow keys.
function CourseSearch({ onSelectCourse }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return TOPIC_NAMES.filter((topic) => topic.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectCourse = (topic) => {
    onSelectCourse(topic);
    setQuery("");
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || matches.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? matches.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = matches[highlightedIndex] ?? matches[0];
      if (pick) selectCourse(pick);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="cm-navbar__search" ref={wrapRef}>
      <SearchIcon />
      <input
        type="text"
        placeholder="Search Courses..."
        value={query}
        onChange={handleChange}
        onFocus={() => query && setOpen(true)}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-autocomplete="list"
      />

      {open && query && (
        <ul className="cm-navbar__search-results" role="listbox">
          {matches.length > 0 ? (
            matches.map((topic, idx) => (
              <li key={topic} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={idx === highlightedIndex}
                  className={`cm-navbar__search-item${
                    idx === highlightedIndex ? " highlighted" : ""
                  }`}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => selectCourse(topic)}
                >
                  {topic}
                </button>
              </li>
            ))
          ) : (
            <li className="cm-navbar__search-empty">No courses found</li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function Navbar({ onMenuClick }) {
  const [activeTopic, setActiveTopic] = useState("DSA");
  // When true, the course view should skip straight to the first
  // lesson that has an MCQ quiz attached, instead of the first lesson.
  const [mcqMode, setMcqMode] = useState(false);
  // Whether a token is present in localStorage (i.e. user is signed in)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkToken = () => setIsLoggedIn(!!localStorage.getItem("token"));

    checkToken();

    // Keep in sync if token changes in another tab, or you dispatch
    // a custom "authChange" event right after login/logout.
    window.addEventListener("storage", checkToken);
    window.addEventListener("authChange", checkToken);

    return () => {
      window.removeEventListener("storage", checkToken);
      window.removeEventListener("authChange", checkToken);
    };
  }, []);

  const onSign = () => {
    navigate("/login");
  };

  const handleTopicTabClick = (topic) => {
    setActiveTopic(topic);
    setMcqMode(false);
  };

  const handlePracticeMcqSelect = (topic) => {
    setActiveTopic(topic);
    setMcqMode(true);
  };

  const handleSearchSelect = (topic) => {
    setActiveTopic(topic);
    setMcqMode(false);
  };

  const activeId = TOPICS[activeTopic];

  return (
    <>
      <div className="cm-navbar">
        <div className="cm-navbar__top">
          <div className="cm-navbar__brand">
            <img
              onClick={() => navigate("/")}
              src="codematrixLogo.png"
              alt="CodeMatrix"
              className="cm-navbar__logo"
            />
          </div>

          <div className="cm-navbar__menu">
            {MENU_ITEMS.map((item) => (
              <button
                key={item}
                className="cm-navbar__menu-item"
                onClick={() => onMenuClick && onMenuClick(item)}
              >
                {item}
                <ChevronIcon />
              </button>
            ))}

            <PracticeMcqMenu onSelectCourse={handlePracticeMcqSelect} />
          </div>

          <CourseSearch onSelectCourse={handleSearchSelect} />

          <div className="cm-navbar__actions">
            {!isLoggedIn && (
              <button className="cm-navbar__signin" onClick={onSign}>
                Sign In
              </button>
            )}

            <button className="cm-navbar__burger" aria-label="Menu">
              <BurgerIcon />
            </button>
          </div>
        </div>

        <div className="cm-navbar__topics">
          {Object.entries(TOPICS).map(([topic, id]) => (
            <a
              key={topic}
              href="#"
              className={topic === activeTopic ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                handleTopicTabClick(topic);
              }}
            >
              {topic}
            </a>
          ))}
        </div>
      </div>

      <div className="dsa-layout">
        <div className="dsa-content">
          <Coursemap id={activeId} topic={activeTopic} startWithMcq={mcqMode} />
        </div>
      </div>
    </>
  );
}